import React, { ReactNode, useEffect, useState } from "react";
import { css, Global } from "@emotion/react";
import { navigate } from "gatsby";

import Banner from "@upsun/react-component-library/src/components/Banner";
import CallToAction from "@upsun/react-component-library/src/components/CallToAction";
import Footer from "@upsun/react-component-library/src/components/Footer";
import Header from "@upsun/react-component-library/src/components/Header";
import JumplinkBar from "@upsun/react-component-library/src/components/JumplinkBar";

import { 
  globalStyles, 
  globalStylesDark, 
  pxToRem, 
  respSpacing,
} from "@upsun/react-component-library/src/styles";

import ThemeContext from "../context/ThemeContext";
import LocaleContext from "../context/LocaleContext";
import ScrollTracker from "../utils/ScrollTracker";

import "../css/fonts.css";
import "../css/layoutFix.css";

import headerData from "../data/header/header-data";
import footerData from "../data/footer/footer-data";
import headerDataFR from "../data/header/header-data-FR";
import footerDataFR from "../data/footer/footer-data-FR";
import headerDataDE from "../data/header/header-data-DE";
import footerDataDE from "../data/footer/footer-data-DE";
import { defaultCTAPropsMap } from "../data/temp-translated-strings";

type SimpleBtn = {
  text: string
  href: string
  shortText?: string
};

type CalBtn = {
  eventTitle: string
  btnLabel?: string
  eventDesc?: string
  startDate: string
  endDate?: string
  startTime?: string
  endTime?: string
  eventLocation?: string
  timezone?: string
};

type Localization = {
  text: string;
  href?: string;
};

type LayoutProps = {
  children: ReactNode
  simpleHeader?: boolean
  simpleFooter?: boolean
  hideBanner?: boolean
  simpleBtn?: SimpleBtn
  calBtn?: CalBtn
  cta?: boolean | {
    text: string
    subtitle?: string
    highlightedText?: string
    btnHref: string
    btnText: string
    btn2Text?: string
    btn2Href?: string
  };
  bottomBar?: boolean
  subNav?: {
    links: {
      text: string
      href: string
    }[]
    matchActiveParent?: boolean
    matchOverride?: string
  }
  locale?: string | null
  spaceAboveFooter?: 'sm' | 'lg' | null,
  localizations?: Localization[],
  location?: string
};

declare global {
  interface Window {
    OnetrustActiveGroups?: string;
  }
}

const Layout: React.FC<LayoutProps> = ({
  children,
  simpleHeader,
  simpleFooter,
  simpleBtn,
  calBtn,
  hideBanner,
  cta,
  bottomBar,
  subNav,
  locale,
  spaceAboveFooter,
  localizations,
  location,
  ...props
}) => {
  // const [isAnnouncementHidden, setisAnnouncementHidden] = useState(true);
  // const currentAnnouncementId = "announceFPIUpdate1112";

  useEffect(() => {
    /* Check if the announcement has already been hidden */
    // if (typeof sessionStorage !== `undefined`) {
    //   if (
    //     sessionStorage.getItem("upsun_hide_announce") ==
    //       currentAnnouncementId ||
    //     hideBanner
    //   ) {
    //     setisAnnouncementHidden(true);
    //   } else {
    //     setisAnnouncementHidden(false);
    //   }
    // }

    /* Begin marketing referral tracking */
    /* Array of parameters that we'll need to reference throughout */
    const relevantParams = [
      "utm_medium",
      "utm_campaign",
      "utm_source",
      "utm_content",
      "utm_term",
      "trial_type",
      "gclid",
    ];

    /* Grabs params from the URL and updates their values in localStorage if applicable */
    const setLocalFromURL = () => {
      const urlQueries = window.location.search;

      if (!urlQueries) {
        return;
      }

      const urlParams = new URLSearchParams(urlQueries);

      relevantParams.forEach((param) => {
        const paramValue = urlParams.get(param);

        if (!paramValue) {
          return;
        }

        localStorage.setItem("rt_" + param, paramValue);
      });
    };

    /* Construct the string of params and values */
    const generateParamString = () => {
      let paramString = "";

      relevantParams.forEach((param) => {
        let stored = localStorage.getItem("rt_" + param);

        if (!stored) {
          return;
        }

        paramString = paramString + "&_" + param + "=" + stored;
      });

      return paramString;
    };

    /* Update attributes in the DOM accordingly */
    // TODO: Add types later
    const updateLinkVals = (elArray: any, attr: any, newParamString: any) => {
      elArray.forEach((el: any) => {
        let origHref = el.getAttribute(attr);
        origHref += origHref.includes("?")
          ? newParamString
          : "?" + newParamString.substring(1);
        el.setAttribute(attr, origHref);
      });
    };

    /* Init */
    // Check consent from onetrust, only if consent exists, store URL params in localStorage.
    let consentGroups = "";
    let consentGroupsArray: string[] = [];

    if (window) {
      consentGroups = window.OnetrustActiveGroups || "";
      consentGroupsArray = consentGroups.split(",");
    }

    if (consentGroupsArray.includes("C0002")) {
      setLocalFromURL();
    }

    let redirectParams = generateParamString();

    if (document.getElementById("mktg-redirect")) {
      if (redirectParams.indexOf("trial_type") < 0) {
        // Set trial_type general if there isn't already a trial_type
        redirectParams = redirectParams + "&_trial_type=general";
      }

      window.location.href =
        "https://auth.upsun.com/register?" + redirectParams.substring(1);
    }

    /* Assuming we didn't redirect, tack the params onto these various other links */
    const linksToRefer = document.querySelectorAll(
      'a[href*="auth.upsun.com"],a[href*="console.upsun.com"]'
    );

    if (linksToRefer) {
      updateLinkVals(linksToRefer, "href", redirectParams);
    }

    //Turn off scrollRestoration
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    //This makes anchor links work on visit. For some reason by default it won't go to the section unless clicked on the page directly.
    if (window) {
      if (document) {
        const hash = window.location?.hash.replace("#", "");
        const anchorLink = hash ? document.getElementById(hash) : null;
        if (anchorLink) {
          anchorLink.scrollIntoView();
        } else {
          window.scrollTo(0, 0);
        }
      } else {
        window.scrollTo(0, 0);
      }
    }

    //Edgee tracking
    // if(window) {
    //   if(document) {
    //     const linksArray = Array.from(document.getElementsByTagName("A"));

    //     const clickedLinkType = (element : HTMLElement) => {
    //       const ctaText = ["Calculate your savings", "Pricing calculator", "Free trial", "Start your free trial", "Get started", "Contact sales", "Talk to sales", "Watch a demo"];

    //       if(element.tagName === "IMG" || element.tagName === "svg") {
    //         return "image";
    //       }
        
    //       if(ctaText.includes(element.innerText)) {
    //         return "cta";
    //       }

    //       return "text";
    //     };

    //     linksArray.map((link) => {
    //       link.addEventListener("click", (e : any) => {
    //         const element = e.target;
    //         const type = clickedLinkType(element);

    //         let href = "";
    //         let label = "";

    //         if (!element.href) {
    //           href = element.parentNode?.href;
    //         } else {
    //           href = element.href;
    //         }

    //         if (type === "image") {
    //           label = `${element.src} ${element.alt}`;
    //         } else {
    //           label = element.innerText;
    //         }

    //         const properties = {
    //           "from": document.location.href,
    //           "to": href,
    //           "label": label,
    //           "type": type,
    //           "position": "body"
    //         }

    //         if(window.edgee) {
    //           window.edgee.track({
    //             "name": "link",
    //             "properties": properties
    //           }, {
    //             "edgee/google-analytics": true
    //           });
    //         }
    //       });
    //     });

    //     const scrollTracker = new ScrollTracker({
    //       thresholds: [25, 50, 75, 100],
    //       debounceTime: 100,
    //       onThresholdReached: (threshold: number) => {
    //           if(window.edgee) {
    //             window.edgee.track({
    //               "name": "scroll",
    //               "properties": {
    //                 "percentage": `${threshold}%`
    //               }
    //             }, {
    //               "edgee/google-analytics": true
    //             });
    //           }
    //       }
    //     });
      
    //   scrollTracker.init();

    //   const videoArray = Array.from(document.getElementsByTagName("VIDEO"));

    //   videoArray.map(video => {
    //     video.addEventListener("play", (e:any) => {
    //       const videoElement = e.target as HTMLVideoElement;
    //       let src = videoElement.src;

    //       if(!src) {
    //         const children = videoElement.childNodes;
    //         if(children.length > 0) {
    //           const sourceElement = children[0] as HTMLSourceElement;
    //           if(sourceElement.tagName === "SOURCE") {
    //             src = sourceElement.src;
    //           }
    //         }
    //       }

    //       if(window.edgee) {
    //         window.edgee.track({
    //           "name": "video",
    //           "properties": {
    //             "title": videoElement.title,
    //             "src": src
    //           }
    //         }, {
    //           "edgee/google-analytics": true
    //         });
    //       }
    //     });
    //   });
    //   }
    // }

    if(typeof window !== `undefined` && !localizations) {
      setDefaultLocation(window.location.pathname);
      setDefaultLocalizations([
        {
          text: "English",
          href: window.location.pathname
        },
        {
          text: "Français"
        },
        {
          text: "Deutsch"
        }
      ]);
    }
  }, [typeof window !== `undefined`]);
  /* End mktg referral tracking */

  // Margin above the footer is based on the presence/absence of the cta by
  // default, but can be overridden by the spaceAboveFooter prop, because who
  // likes consistency anyway? :D
  const preFooterMargin = css`
    ${spaceAboveFooter ?
      (spaceAboveFooter === 'sm' ? 
        respSpacing('margin-block-end', 'm') : 
        respSpacing('margin-block-end', '3xl')
      )
      :
      (cta ? 
        respSpacing('margin-block-end', 'm') : 
        respSpacing('margin-block-end', '3xl')
      )
    }
  `;

  const positionRelative = css`
    position: relative;
  `;

  const bottomBarMargin = css`
    height: ${pxToRem(90)};
  `;

  const usedLocale = locale || "en";

  const defaultCTAProps = defaultCTAPropsMap[usedLocale];

  let callToActionProps;

  if(typeof cta === "boolean") {
    callToActionProps = defaultCTAProps;
  } else {
    callToActionProps = {
      text: cta?.text || defaultCTAProps.text,
      highlightedText: cta?.highlightedText || undefined,
      subtitle: cta?.subtitle || undefined,
      btnHref: cta?.btnHref || defaultCTAProps.btnHref,
      btnText: cta?.btnText || defaultCTAProps.btnText,
      btn2Href: cta?.btn2Href || undefined,
      btn2Text: cta?.btn2Text || undefined,
    }
  }

  const [defaultLocalizations, setDefaultLocalizations] = useState([
    {
      text: "English",
      href: "/"
    },
    {
      text: "Français"
    },
    {
      text: "Deutsch"
    }
  ]);
  const [defaultLocation, setDefaultLocation] = useState("/");

  const headerDataLocales: { [key: string]: any } = {
    "fr": headerDataFR,
    "de": headerDataDE,
    "en": headerData
  };

  const footerDataLocales: { [key: string]: any } = {
    "fr": footerDataFR,
    "de": footerDataDE,
    "en": footerData
  };
  
  return (
    <>
      <LocaleContext.Consumer>
        {(locale: any) => (
          <ThemeContext.Consumer>
            {(theme: any) => {
              if(theme && theme.name) {
                if(typeof window !== `undefined`) {
                  document.getElementsByTagName('body')[0].classList.add('theme',theme.name);
                }
              }

              const opacityAnimation = css`
                html {
                  scroll-padding-top: 200px;
                }
                body {
                  ${theme && theme.name ? `opacity: 1;` : ``};
                }
              `;

              return (
                <>
                  <Global
                    styles={
                      [opacityAnimation, theme.name === "light" ? globalStyles : globalStylesDark]
                    }
                  />
              {/* {!isAnnouncementHidden && (
                <Banner
                  text="Git going on Upsun with up to $19 off every month*"
                  linkText="Find out more"
                  href="/blog/first-project-incentive/"
                  icon={
                    <svg
                      width="27"
                      height="26"
                      viewBox="0 0 27 26"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="% icon">
                        <path
                          id="Vector"
                          d="M4.15337 6.62995C4.15337 5.85723 4.46241 5.11616 5.01252 4.56976C5.56263 4.02337 6.30873 3.71641 7.0867 3.71641H8.42003C9.19456 3.71597 9.93749 3.41128 10.4867 2.86883L11.42 1.94179C11.6926 1.66951 12.0167 1.45344 12.3737 1.30599C12.7306 1.15855 13.1134 1.08264 13.5 1.08264C13.8866 1.08264 14.2694 1.15855 14.6264 1.30599C14.9833 1.45344 15.3074 1.66951 15.58 1.94179L16.5134 2.86883C17.0626 3.41128 17.8055 3.71597 18.58 3.71641H19.9134C20.6913 3.71641 21.4374 4.02337 21.9875 4.56976C22.5377 5.11616 22.8467 5.85723 22.8467 6.62995V7.95428C22.8471 8.72359 23.1539 9.4615 23.7 10.007L24.6334 10.934C24.9075 11.2048 25.125 11.5267 25.2735 11.8813C25.4219 12.2358 25.4984 12.616 25.4984 13C25.4984 13.384 25.4219 13.7642 25.2735 14.1187C25.125 14.4733 24.9075 14.7952 24.6334 15.066L23.7 15.993C23.1539 16.5385 22.8471 17.2764 22.8467 18.0457V19.3701C22.8467 20.1428 22.5377 20.8839 21.9875 21.4303C21.4374 21.9766 20.6913 22.2836 19.9134 22.2836H18.58C17.8055 22.284 17.0626 22.5887 16.5134 23.1312L15.58 24.0582C15.3074 24.3305 14.9833 24.5466 14.6264 24.694C14.2694 24.8415 13.8866 24.9174 13.5 24.9174C13.1134 24.9174 12.7306 24.8415 12.3737 24.694C12.0167 24.5466 11.6926 24.3305 11.42 24.0582L10.4867 23.1312C9.93749 22.5887 9.19456 22.284 8.42003 22.2836H7.0867C6.30873 22.2836 5.56263 21.9766 5.01252 21.4303C4.46241 20.8839 4.15337 20.1428 4.15337 19.3701V18.0457C4.15292 17.2764 3.84617 16.5385 3.30003 15.993L2.3667 15.066C2.09257 14.7952 1.87502 14.4733 1.72658 14.1187C1.57813 13.7642 1.50171 13.384 1.50171 13C1.50171 12.616 1.57813 12.2358 1.72658 11.8813C1.87502 11.5267 2.09257 11.2048 2.3667 10.934L3.30003 10.007C3.84617 9.4615 4.15292 8.72359 4.15337 7.95428V6.62995Z"
                          fill="#D5F800"
                          stroke="black"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          id="Vector_2"
                          d="M9.48669 16.9601L17.4867 9.0141"
                          stroke="black"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          id="Vector_3"
                          d="M10.1534 10.3384C10.5216 10.3384 10.82 10.042 10.82 9.67627C10.82 9.31056 10.5216 9.0141 10.1534 9.0141C9.78517 9.0141 9.48669 9.31056 9.48669 9.67627C9.48669 10.042 9.78517 10.3384 10.1534 10.3384Z"
                          fill="black"
                          stroke="black"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          id="Vector_4"
                          d="M16.82 16.9595C17.1882 16.9595 17.4867 16.663 17.4867 16.2973C17.4867 15.9316 17.1882 15.6351 16.82 15.6351C16.4519 15.6351 16.1534 15.9316 16.1534 16.2973C16.1534 16.663 16.4519 16.9595 16.82 16.9595Z"
                          fill="black"
                          stroke="black"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                    </svg>
                  }
                  type={"strong"}
                  id={currentAnnouncementId}
                  closed={isAnnouncementHidden}
                />
              )} */}
              <Header
                simpleHeader={simpleHeader}
                simpleBtn={simpleBtn}
                calBtn={calBtn}
                data={headerDataLocales[usedLocale]}
                navigate={navigate}
                css={subNav ? positionRelative : []}
                localizations={localizations || defaultLocalizations}
                location={location || defaultLocation}
              />
              {subNav && subNav?.links?.length > 0 && 
                <JumplinkBar 
                  mode="secondary" 
                  links={subNav?.links} 
                  matchActiveParent={subNav?.matchActiveParent}
                  matchOverride={subNav?.matchOverride}
                />
              }
              <main css={positionRelative}>{children}</main>
              <div css={preFooterMargin}>
                {cta && (
                  <CallToAction {...callToActionProps} />
                )}
              </div>
              <Footer 
                changeTheme={theme.changeTheme} 
                data={footerDataLocales[usedLocale]}
                simpleFooter={simpleFooter || false}
                handleSubmit={async email => {
                  const formSubmit = {
                    input: [
                      {
                        leadFormFields: {
                          utmcampaign: "",
                          utmcampaignID: "",
                          utmsource: "",
                          utmmedium: "organic_social",
                          GCLID__c: "",
                          utm_term__c: "",
                          email: email
                        },
                        visitorData: {
                          pageURL: encodeURI(`https://upsun.com${location}`) || "https://upsun.com/preferences/"
                        }
                      }
                    ],
                    formId: 1169
                  };
                  await fetch(`/submit`, {
                    method: "POST",
                    body: JSON.stringify(formSubmit),
                    headers: { "Content-Type": "application/json" }
                  });
                }}
              />
              {bottomBar && (
                <div
                  css={bottomBarMargin}
                />
              )}
            </>
          );
        }}
      </ThemeContext.Consumer>
        )}
      </LocaleContext.Consumer>
    </>
  );
};

export default Layout;
